import { ITeamMember } from "@/utils/zod/contactFormValidation";
import { Box, Typography, Modal, Stack, Button, CircularProgress } from "@mui/material";
import { useCustomMutation, useList, useModal, useNotification, useTranslate } from "@refinedev/core";
import { Dispatch, SetStateAction, useState } from "react";
import { FormProvider } from "react-hook-form";
import { RHFAutocomplete } from "../form";
import { useForm } from "@refinedev/react-hook-form";
import { NoOutstandingModal } from "./NoOutstandingModal";

// Define interface for member type
interface Member {
  id: string;
  name: string;
  phone_number: string;
}

interface CompanyWithoutOutstanding {
  ledger_name: string;
  members: Member[];
}

interface SendOutstandingModalProps { 
  enhancedRowSelection: { 
    ledger_name: string, 
    members?: { 
      id: string, 
      name?: string | null, 
      phone_number?: number | string
    }[] | undefined, 
    send_outstanding_to_accounts?: (string | { 
      id: string, 
      name?: string | null, 
      phone_number?: number | string 
    })[] | undefined
  }[], 
  setRowSelection: Dispatch<SetStateAction<{}>>, 
  visible: boolean, 
  close: () => void 
}

export const SendOutstandingModal = ({ enhancedRowSelection, setRowSelection, visible, close }: SendOutstandingModalProps) => {

  const { visible: noOutstandingModalVisible, show: noOutstandingModalShow, close: noOutstandingModalClose } = useModal();

  // Add loading state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [companiesWithoutSendToOutstandingAccounts, setCompaniesWithoutSendToOutstandingAccounts] = useState<CompanyWithoutOutstanding[]>([]);

  const { mutateAsync: sendOutstanding } = useCustomMutation();

  const { open } = useNotification();
  const t = useTranslate();

  const formValues = useForm({
    defaultValues: {
      sender_account_id: "",
    },
  })

  const senderAccountId = formValues.watch('sender_account_id');

  // Create a handler for sending outstanding notices
  const handleSendOutstanding = formValues.handleSubmit(async (data: any) => {
    // Get the selected ledger names
    setIsSubmitting(true);

    const selectedLedgerNames = enhancedRowSelection.map(
      company => company.send_outstanding_to_accounts && company.send_outstanding_to_accounts.length > 0 ? company.ledger_name : null
    ).filter(Boolean);

    // Prepare companies without send_outstanding_to_accounts for display in NoOutstandingModal
    const withoutOutstandingAccounts = enhancedRowSelection
      .filter(company => company.send_outstanding_to_accounts && company.send_outstanding_to_accounts.length === 0)
      .map(company => ({
        ledger_name: company.ledger_name,
        members: (company.members || []).map(member => ({
          id: member.id,
          // Ensure name and phone_number are strings as required by NoOutstandingModal
          name: member.name?.toString() || "",
          phone_number: member.phone_number?.toString() || ""
        }))
      }));
    
    setCompaniesWithoutSendToOutstandingAccounts(withoutOutstandingAccounts);

    try {
      selectedLedgerNames.length > 0 && (await sendOutstanding({
        url: "/companies/send-outstanding-to-accounts",
        method: "post",
        values: {
          sender_account_id: data.sender_account_id,
          companies: selectedLedgerNames,
        },
      }, {
        onSuccess: (response) => {
          response.data?.forEach((response: any) => {
            if (response.status === "error") {
              open?.({
                message: typeof response.message === "string" ? response.message : response.message.error,
                description: `Error sending outstanding for ${response.ledger_name}`,
                type: "error",
              })
            } else {
              open?.({
                message: response.message,
                description: `Successfully sent outstanding for ${response.ledger_name}`,
                type: "success",
              })
            }
          })
        },
        onError: (errors) => {
          if (Array.isArray(errors.response.data)) {
            errors.response.data.forEach((error: any) => {
              open?.({
                message: typeof error.message === 'string' ? error.message : error.message.error,
                description: `Error sending outstanding for ${error.ledger_name}`,
                type: "error",
              })
            })
          } else {
            open?.({
              message: "Error sending outstanding",
              description: errors.message,
              type: "error",
            })
          }
        },
      }))
      withoutOutstandingAccounts.length > 0 && noOutstandingModalShow()
    } finally {
      setIsSubmitting(false);
      setRowSelection({});
      withoutOutstandingAccounts.length <= 0 && formValues.reset();
      close();
    }
  });

  const { data: teamMembers } = useList<ITeamMember>({
    resource: "accounts/team-members",
  });
  return (
    <>
      <Modal open={visible} onClose={isSubmitting ? undefined : close}>
        <FormProvider {...formValues}>
          <Box
            component="form"
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 700,
              bgcolor: 'background.paper',
              boxShadow: 24,
              p: 4,
              borderRadius: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              maxHeight: '90vh',
            }}
          >
            <Typography variant="h6" gutterBottom>
              Select Sender Phone-number
            </Typography>
            <Box>
              <RHFAutocomplete
                name="sender_account_id"
                label={t("broadcast.fields.senderPhone")}
                options={(teamMembers?.data ?? [])?.map((member: ITeamMember) => ({ label: `${member.name ? `${member.name}: ` : ""}${member.user?.phone_number}`, value: member.id })) ?? []}
                multiple={false}
                sx={{ flex: 1 }}
                createNew={false}
                required
                size="small"
              />
            </Box>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="outlined" onClick={() => { formValues.reset(); close(); }} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSendOutstanding}
                disabled={!senderAccountId || isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {isSubmitting ? "Sending..." : "Send"}
              </Button>
            </Stack>
          </Box>
        </FormProvider>
      </Modal>
      <NoOutstandingModal visible={noOutstandingModalVisible} close={noOutstandingModalClose} data={companiesWithoutSendToOutstandingAccounts} senderAccountId={senderAccountId} onSubmit={formValues.reset}/>
    </>
  )
}